import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

export type OverlappingEventIndicatorVariantType = 'dot' | 'icon' | 'full';

export const getOverlappingEventIndicatorVariant = (apptDuration: number): OverlappingEventIndicatorVariantType => {
  if (apptDuration <= 15) return 'dot';
  if (apptDuration <= 30) return 'icon';
  return 'full';
};

interface Props {
  variant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  visible?: boolean;
  borderless?: boolean;
  marginLeft?: string;
}

export const OverlappingEventIndicator = ({
  variant,
  visible = false,
  isYesterdayEvent = false,
  borderless = false,
  marginLeft,
}: Props) => {
  const { t } = useTranslation('schedule');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  if (!isScheduleV3FlagEnabled) return null;

  if (!variant || !visible) return null;

  if (variant === 'dot') {
    return (
      <span
        aria-label={t('Multi-day appointment')}
        css={[containerStyle(borderless), dotStyle, { marginLeft: theme.spacing(0.5) }]}
      />
    );
  }

  // For icon/full variants
  const ariaLabel = isYesterdayEvent
    ? t('Appointment continued from previous day')
    : t('Appointment continues to next day');

  return (
    <span
      aria-label={ariaLabel}
      css={[
        containerStyle(borderless),
        indicatorStyle,
        { marginLeft: marginLeft ?? theme.spacing(variant === 'icon' ? 0.5 : 0) },
      ]}
    >
      {isYesterdayEvent ? (
        <Icon name='back-small' size={8} css={{ minWidth: theme.spacing(1) }} />
      ) : (
        <Text size='small'>+1</Text>
      )}
      {variant === 'full' && <Text size='small'>{isYesterdayEvent ? t('Continued') : t('Day')}</Text>}
    </span>
  );
};

const containerStyle = (borderless: boolean) =>
  css({
    display: 'inline-flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    borderRadius: theme.borderRadius.small,
    background: theme.colors.indigo20,
    color: theme.colors.indigo90,
    border: borderless ? 'none' : `1px solid ${theme.colors.indigo50}`,
  });

const dotStyle = css({
  borderRadius: theme.borderRadius.full,
  padding: theme.spacing(0.5),
  maxHeight: theme.spacing(1),
});

const indicatorStyle = css({
  height: theme.spacing(2),
  padding: theme.spacing(0, 0.5),
  maxWidth: 'max-content',
});
